import * as React from 'react';
import * as ReactDOM from 'react-dom';
import App from './App';
import { StyleSheetManager } from 'styled-components';
import { getDocumentRoots } from './utils/getDocumentRoots';
import { Manifest } from './configuration/Manifest';
import { generateGuid } from './utils/generateGuid';

const renderApp = (root: HTMLElement, intlSupported) => {
    let src = '';
    if (Manifest.currentScript) {
        src = Manifest.currentScript.src;
    }
    // Get the marked loading element supplied to the embed
    // this will be used while the bootstrapping takes place
    // Once once the react render takes place, this element and any other elements with in the embed will be replaced
    const embedLoadingElement = root.firstElementChild;
    // Attach a shadow dom to the element
    const shadow = root.attachShadow({ mode: 'open' });

    // Create div element for react to render into
    const reactRoot = document.createElement('div');
    reactRoot.setAttribute('id', generateGuid());
    // Append react root to shadow root
    shadow.appendChild(reactRoot);
    // Create div element for the styles to render into
    const stylesRoot = document.createElement('div');
    stylesRoot.setAttribute('id', generateGuid());
    shadow.appendChild(stylesRoot);

    Object.defineProperty(reactRoot, 'ownerDocument', { value: shadow });
    const methods = ['createElement', 'createElementNS', 'createTextNode'];
    for (const method of methods) {
        // @ts-ignore
        shadow[method] = (...args) => document[method](...args);
    }

    // Set initialized property, so that we do not re-render an already mounted Fund Center
    // when ApplicationEmbed adds another Fund Center to the page
    root.dataset.initialized = 'true';

    ReactDOM.render(
        // Set intlSupported prop
        // Spread data-attributes from root element as props
        // Pass script tag src as prop
        <StyleSheetManager target={stylesRoot}>
            <App
                intlSupported={intlSupported}
                scriptSrc={src}
                {...root.dataset}
                root={root}
                shadowRoot={shadow}
                embedLoadingElement={embedLoadingElement}
            />
        </StyleSheetManager>,
        reactRoot
    );
};

// Dynamic import Intl polyfill
const loadIntlPolyfill = async () => {
    await import(/* webpackChunkName: "intl" */ 'intl');
};

const renderApps = (intlSupported: boolean) => {
    const roots = getDocumentRoots();
    for (const root of roots) {
        if (root.dataset.initialized !== 'true') {
            renderApp(root, intlSupported);
        }
    }
};

function initialize() {
    // Load Intl polyfill where required then render app
    if (!(window as any).Intl) {
        console.info('Intl API unsupported');
        loadIntlPolyfill();
        renderApps(false);
    } else {
        console.info('Intl API supported');
        renderApps(true);
    }
}

document.addEventListener('ksys-app-embed-added', (event: any) => {
    const {
        ksysAppTemplateId,
        applicationEmbedComponent,
    } = event.target.dataset;
    // When we receive ksys-app-embed-added event with applicationEmbedComponent set to true
    // We knew to initialize the fund center if it was added to the page after load
    if (
        ksysAppTemplateId === 'ksys-app-fund-center' &&
        applicationEmbedComponent === 'true'
    ) {
        initialize();
    }
});

initialize();



// WEBPACK FOOTER //
// ./src/start.tsx